"use client";
import Image from "@/components/atoms/Image";
import React, { useEffect, useState } from "react";
import Button from '@/components/atoms/button';
import { useRouter, useSearchParams } from "next/navigation";
import Typography from "@/components/atoms/typography";
import Service from '@/service';
import Spinner from "@/components/organisms/spinner";
import Link from "@/components/atoms/link";
import getCookieValue from "@/utils/cookie";

const OrderConfirmation = () => {
    const router = useRouter();
    const searchParams = useSearchParams();
    const orderNumber = searchParams.get('orderNumber');
    const locale = getCookieValue('locale');


    const getCountryName = (countryCode: string | null | undefined): string => {
        if (!countryCode) return 'N/A';
        const code = countryCode.toUpperCase();
        switch (code) {
            case 'AU':
                return 'Australia';
            case 'NZ':
                return 'New Zealand';
            default:
                return countryCode;
        }
    };

    const [cartDetails, setCartDetails] = useState<any>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (typeof window !== 'undefined') {
            const handleBackButton = (event: PopStateEvent) => {
                event.stopImmediatePropagation();

                window.location.href = '/';
            };

            const currentState = { page: 'order-confirmed' };
            window.history.pushState(currentState, '', window.location.href);

            window.addEventListener('popstate', handleBackButton, { capture: true });

            return () => {
                window.removeEventListener('popstate', handleBackButton, { capture: true });
            };
        }
    }, []);

    useEffect(() => {
        const fetchOrderDetails = async () => {
            try {
                if (!orderNumber) {
                    console.error("Order number not found in URL");
                    setLoading(false);
                    return;
                }
                const result = await Service.checkout.orderDetails({ orderNumber });
                if (result?.data) {
                    setCartDetails(result.data);
                }
            } catch (error) {
                console.error("Failed to create order:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchOrderDetails();
    }, [orderNumber]);

    if (loading) {
        return (
            <>
                <Spinner />
                <div className="overlay fixed left-0 top-0 z-50 h-full w-full bg-gray-800 opacity-75"></div>
            </>
        );
    }

    const renderCTAs = () => {
        return <div className="flex sm:flex-row flex-col max-w-[443px] items-center gap-4 lg:mt-10">
            <Button
                size="full"
                className="h-[48px] !cursor-pointer  !bg-black text-[18px] font-normal leading-5"
                variant="primary"
                onClick={() => router.push('/manage-orders')}
            >
                Manage Orders
            </Button>
            <Button
                size="full"
                className="h-[48px] !cursor-pointer text-[18px] font-normal leading-5"
                variant="secondary"
                onClick={() => router.push('/')}

            >
                Return to Home
            </Button>

        </div>
    };
    console.log("cartDetails?.orderDetails?.orderSummary?.lineItems", cartDetails?.orderDetails?.orderSummary?.lineItems);
    return (
        <div className="lg:px-10">
            <div className="max-w-[1480px] mx-auto px-4 bg-[#F7F7F7] lg:bg-white py-8 lg:py-[80px]">
                <div className="flex lg:flex-row flex-col gap-5 lg:gap-[85px]">
                    <div className="lg:w-[49%] lg:pt-[78px] ">
                        <div className="bg-white lg:bg-none pt-[22px] pl-[31px] pr-[21px] lg:p-0 ">
                            <Typography className="!text-[20px] sm:!text-[44.9px] !font-bold text-black leading-[100%]">
                                Thank you for your purchase!
                            </Typography>

                            <Typography className="!font-medium text-[16px] md:!text-[22.9px] pb-[33px] !text-black mt-[20px] sm:mt-[44px] leading-normal">
                                Your order has been submitted. Keep an eye out for an order
                                confirmation email. Alternatively, you can track your order by
                                clicking the button below.
                            </Typography>
                        </div>
                        <div className="pt-[35px] mt-[14px] pb-[20px] sm:!pt-[70px] pl-[31px] pr-[21px] lg:p-0 bg-white lg:mt-0 leading-[100%]">
                            <Typography className="!font-bold text-[20px] sm:!text-[26px]">
                                Customer Information
                            </Typography>
                            <div className="space-y-[13px]">
                                <div className="text-[16px] md:text-[20px]  pt-[39px]">
                                    <span className="w-28 inline-block text-black font-bold">
                                        Name
                                    </span>
                                    <span className="font-medium">
                                        {[cartDetails?.orderDetails?.shippingAddress?.firstName, cartDetails?.orderDetails?.shippingAddress?.lastName]
                                            .filter(Boolean)
                                            .join(' ') || 'N/A'}
                                    </span>
                                </div>

                                <div className="text-[16px] md:text-[20px]">
                                    <span className="w-28 inline-block text-black font-bold">
                                        Address
                                    </span>
                                    <span className="font-medium">
                                        {[cartDetails?.orderDetails?.shippingAddress?.streetNumber, cartDetails?.orderDetails?.shippingAddress?.streetName].filter(Boolean).join(' ') || cartDetails?.orderDetails?.shippingAddress?.address || cartDetails?.orderDetails?.shippingAddress?.addressText || 'N/A'}
                                    </span>
                                </div>
                                {/* <div className="text-[16px] md:text-[20px]">
                                    <span className="w-28 inline-block text-black font-bold">
                                        Suburb
                                    </span>
                                    <span className="font-medium">{cartDetails?.orderDetails?.shippingAddress?.suburb || 'N/A'}</span>
                                </div> */}
                                <div className="text-[16px] md:text-[20px]">
                                    <span className="w-28 inline-block text-black font-bold">
                                        City
                                    </span>
                                    <span className="font-medium">{cartDetails?.orderDetails?.shippingAddress?.city || 'N/A'}</span>
                                </div>
                                <div className="text-[16px] md:text-[20px]">
                                    <span className="w-28 inline-block text-black font-bold">
                                        State
                                    </span>
                                    <span className="font-medium">{cartDetails?.orderDetails?.shippingAddress?.state || 'N/A'}</span>
                                </div>
                                <div className="text-[16px] md:text-[20px]">
                                    <span className="w-28 inline-block text-black font-bold">
                                        Postcode
                                    </span>
                                    <span className="font-medium">{cartDetails?.orderDetails?.shippingAddress?.postcode || 'N/A'}</span>
                                </div>
                                <div className="text-[16px] md:text-[20px]">
                                    <span className="w-28 inline-block text-black font-bold">
                                        Country
                                    </span>
                                    <span className="font-medium">{getCountryName(cartDetails?.orderDetails?.shippingAddress?.country)}</span>
                                </div>
                                <div className="text-[16px] md:text-[20px]">
                                    <span className="w-28 inline-block text-black font-bold mb-[17px]">
                                        Phone
                                    </span>
                                    <span className="font-medium">{cartDetails?.orderDetails?.shippingAddress?.phone || 'N/A'}</span>
                                </div>
                            </div>
                            <div className="lg:block hidden">
                                {renderCTAs()}
                            </div>

                        </div>
                    </div>
                    <div className="bg-white lg:w-[51%] lg:bg-[#F3EDE9] lg:pt-[78px] pt-[11px] lg:px-8 2xl:px-[100px] lg:pb-[83px] pb-[33px] pl-[31px] pr-[21px]">
                        <Typography className="!font-semibold text-[20px] sm:!text-[28px] !ml-[1px] lg:!ml-[3px] leading-[100%]">
                            Order Summary
                        </Typography>

                        <div className="mb-[30px] lg:mb-[61.57px] pt-[18px] mb-lg ml-[1px] lg:ml-[3px]">
                            <span className="text-[16px] md:text-[20px] font-bold text-black inline-block mr-2">Date</span>
                            <span className="text-[16px] md:text-[20px] font-medium text-black">
                                {cartDetails?.orderDetails?.orderSummary?.orderDate}
                            </span>
                        </div>
                        {cartDetails?.orderDetails?.orderSummary?.lineItems?.map((item: any) => {
                            return <div key={item?.lineItemId}>
                                <div className="flex gap-[17px] lg:gap-[36px] mb-8">

                                    <div className="flex-shrink-0">
                                        {item?.primaryImage?.src ? (
                                            <Link link={item?.url}>
                                                <Image
                                                    className="w-[120px] cursor-pointer h-[120px] lg:w-[163px] lg:h-[163px] object-cover"
                                                    alt={item?.name as string}
                                                    src={item?.primaryImage?.src}
                                                    width={163}
                                                    height={163}
                                                /></Link>
                                        ) : (
                                            <Link link={item?.url}>
                                                <Image
                                                    src="/images/No_product_Image.jpg"
                                                    className="w-[120px]  cursor-pointer h-[120px] lg:w-[163px] lg:h-[163px] object-cover"
                                                    alt="empty product image"
                                                    width={163}
                                                    height={163}
                                                /></Link>
                                        )}
                                    </div>

                                    <div>
                                        <Link link={item?.url}>

                                            <Typography className="text-[17px] sm:text-[20px] font-semibold text-black leading-[100%]">
                                                {item?.name}
                                            </Typography></Link>
                                        <Link link={item?.url}>
                                            <Typography className="text-[16px] md:!text-[18px] font-normal sm:font-light !text-black mt-[11px] leading-[100%]">
                                                {`Product Code: ${item?.itemCode}`}
                                            </Typography>
                                        </Link>

                                        <Typography className="text-[16px] md:!text-[18px] font-medium md:font-semibold !text-black mt-[12.7px] leading-[100%]">
                                            {`Price per unit: ${item?.price ? item?.price?.displayAmount : "N/A"}`}
                                        </Typography>
                                        <p className="text-[15.5px]] sm:text-[18px] font-medium text-[#828282] mt-[18.29px] leading-[100%]">
                                            QTY:{" "}
                                            <span className="text-[16px] md:text-[18px] ml-6 text-black font-medium">
                                                {item?.quantityInCart}
                                            </span>
                                        </p>
                                    </div>
                                </div>
                                <hr className="border-t border-[#D3D3D3] mb-8" />
                            </div>
                        })}


                        <div className="ml-auto max-w-[340px]">
                            <div className="">
                                <div className="flex justify-between">
                                    <span className="font-medium text-black text-[16px] md:text-[18px] text-right min-w-[107px]">Sub Total</span>
                                    <span className="font-semibold text-black text-[20px] sm:text-[24px] leading-[100%]">
                                        {cartDetails?.orderDetails?.orderSummary?.orderTotalPrice?.displayAmount || '$0.00'}
                                    </span>
                                </div>
                                <div className="flex justify-between mt-[11px]">
                                    <span className="font-medium text-black text-[16px] md:text-[18px] text-right min-w-[107px]">GST</span>
                                    <span className="font-semibold text-black text-[20px] sm:text-[24px] leading-[100%]">
                                        {cartDetails?.orderDetails?.orderSummary?.gstPrice?.displayAmount || '$0.00'}
                                    </span>
                                </div>
                                <div className="flex justify-between mt-[9px]">
                                    <span className="font-medium text-black text-[16px] md:text-[18px] text-right min-w-[107px]">
                                        Delivery Fee
                                    </span>
                                    <span className="font-semibold text-black text-[20px] sm:text-[24px] leading-[100%]">$0.00</span>
                                </div>

                                {(cartDetails?.orderDetails?.orderSummary?.vouchers ?? [])
                                    .filter((v: any) => v?.discount?.amount > 0)
                                    .map((v: any, i: number) => (
                                        <div key={i} className="flex justify-between mt-[9px]">
                                            <span className="font-medium text-[#E32C53] text-[16px] md:text-[18px] text-right min-w-[107px]">Discount</span>
                                            <span className="font-semibold text-[#E32C53] text-[20px] sm:text-[24px] leading-[100%]">-{v.discount.displayAmount}</span>
                                        </div>
                                    ))}
                            </div>

                            <hr className="border-t border-[#D3D3D3] mt-[30px] mb-[30px]" />

                            <div className="flex justify-between items-center">
                                <span className="text-[17px] sm:text-[22px] font-bold sm:font-medium text-black text-right min-w-[107px]">Total</span>
                                <span className="text-[20px] sm:text-[28px] font-semibold text-black leading-[100%]">
                                    {cartDetails?.orderDetails?.orderSummary?.orderTotalPriceWithGST?.displayAmount || '$0.00'}
                                </span>
                            </div>

                            {locale === 'au' ? (
                                <p className='text-black font-light text-end !pt-[9px] text-[16px] md:text-[18px] leading-[100%]'>Price includes GST & GST Free Products</p>
                            ) : (
                                <p className='text-black font-light text-end !pt-[9px] text-[16px] md:text-[18px] leading-[100%]'>Price includes GST</p>
                            )}


                        </div>
                    </div>
                    <div className="lg:hidden">
                        {renderCTAs()}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default OrderConfirmation;